import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ScrollText, Users, Scale, CalendarDays, ShieldCheck, ClipboardList, PenLine, Building2 } from "lucide-react";
import { IMAGES, DISCIPLINES } from "../data/content";
import { Reveal } from "../components/shared/Reveal";
import { PrimaryButton, GhostButton } from "../components/shared/Buttons";

const INFO_ROWS = [
  { icon: Building2, label: "Participating Organisation", value: "To be announced" },
  { icon: ScrollText, label: "Competition Format", value: "To be confirmed with the participating organisation" },
  { icon: Users, label: "Age Divisions", value: "Coming Soon" },
  { icon: Scale, label: "Weight Divisions", value: "Coming Soon" },
  { icon: ShieldCheck, label: "Rules", value: "Governed by the participating organisation" },
  { icon: CalendarDays, label: "Schedule", value: "Coming Soon" },
  { icon: ClipboardList, label: "Officials", value: "Appointed by the participating organisation" },
  { icon: PenLine, label: "Registration", value: "Opens soon — details to be announced" },
];

export default function DisciplineDetail() {
  const { slug } = useParams();
  const discipline = DISCIPLINES.find((d) => d.slug === slug);
  if (!discipline) return <Navigate to="/disciplines" replace />;

  const others = DISCIPLINES.filter((d) => d.slug !== slug);

  return (
    <>
      <section data-testid="discipline-hero" className="relative isolate overflow-hidden pb-16 pt-36 sm:pb-24 sm:pt-44">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES[discipline.image]} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/75 to-ink" />
          <div className="absolute inset-0 bg-[radial-gradient(700px_circle_at_80%_20%,rgba(220,38,38,0.14),transparent_60%)]" />
        </div>
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal y={16}>
            <Link
              to="/disciplines"
              data-testid="back-to-disciplines-link"
              className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 transition-colors hover:text-kred"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All Martial Arts
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-mono text-xs font-bold uppercase tracking-[0.35em] text-gold">
              {discipline.origin} • {discipline.family}
            </p>
            <h1 className="mt-4 font-display text-6xl uppercase leading-[0.9] tracking-tight text-bone sm:text-8xl">
              {discipline.name}
            </h1>
            <p className="mt-4 font-head text-lg font-semibold text-kred">{discipline.tag}</p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{discipline.detail}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 sm:pb-32">
        <Reveal>
          <div className="grid gap-px overflow-hidden border border-white/5 bg-white/5 sm:grid-cols-2 lg:grid-cols-4">
            {INFO_ROWS.map((row) => (
              <div
                key={row.label}
                data-testid={`info-${row.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-ink-soft p-6"
              >
                <row.icon className="h-5 w-5 text-kred" />
                <h2 className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                  {row.label}
                </h2>
                <p className="mt-2 text-sm font-medium leading-relaxed text-slate-200">{row.value}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 clip-notch border border-gold/20 bg-gold/[0.04] p-8 sm:p-10">
          <p data-testid="discipline-registration-note" className="text-sm leading-relaxed text-slate-300 sm:text-base">
            Detailed competition information for {discipline.name} — including format, divisions,
            rules and registration — will be published once confirmed with the discipline's
            participating organisation.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <PrimaryButton to="/register" testId="discipline-register-button">
              Register Interest
            </PrimaryButton>
            <GhostButton to="/contact" testId="discipline-contact-button">
              Contact Kumite Games
            </GhostButton>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-16">
          <h2 className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-slate-500">
            Explore other disciplines
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {others.map((d) => (
              <Link
                key={d.slug}
                to={`/disciplines/${d.slug}`}
                data-testid={`related-discipline-${d.slug}`}
                className="border border-white/10 px-5 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-300 transition-all duration-300 hover:border-kred hover:text-kred"
              >
                {d.name}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>
    </>
  );
}
